/* eslint-disable import/no-named-as-default */
import { openModal } from "action/modal/openModal";
import { ICompactProduct } from "api/interfaces/get-page-details/common/CompactProduct.type";
import { IPageContent } from "api/interfaces/static-content.type";
import classNames from "classnames";
import VizSensor from "component/design-system/VizSensor";
import { BUTTON_TYPE } from "component/design-system/buttons/PAButton";
import HorizontalSeparator from "component/design-system/horizontal-separator/HorizontalSeparator";
import PAText, { FONT_SIZE } from "component/design-system/typography/PAText/PAText";
import LoaderSkeleton from "component/loader-skeleton/LoaderSkeleton";
import ProductHeaderRightSection from "component/product/V2/ProductHeaderRightSection";
import ProductOverview from "component/product/V2/overview/ProductOverview";
import matchMediaConst from "constant/matchmedia";
import { useCustomScreenSize } from "hoc/useScreenSize";
import { RootState } from "interfaces/generics.type";
import ViewCorrectProducts from "modules/fitment/view-correct-products/ViewCorrectProducts";
import { useTranslation } from "next-i18next";
import dynamic from "next/dynamic";
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import HideIf from "util/HideIf";
import { isApparelProduct } from "util/product/apparel";
import { getProductHeading } from "util/product/get-product-heading";
import ProductImageSection from "../../component/product/V2/image-section/ProductImageSection";
import ProductDescription from "./ProductDescription";
import GuaranteedFitProducts from "./guaranteed-fit-products/GuaranteedFitProducts";
import styles from "./product.module.scss";
import ShowMoreAndLess from "./showMoreAndLess/ShowMoreAndLessContainer";
import Sitejabber from "./sitejabber/Sitejabber";

const ProductPageStickyHeader = dynamic(() => import(/* webpackChunkName: "product-page-sticky-header" */"container/header/ProductPageStickyHeader/ProductPageStickyHeader"));
const ProductFitment = dynamic(() => import(/* webpackChunkName: "product-Fitment" */ "modules/fitment/ProductFitment"), { loading: () => <LoaderSkeleton height={95} count={1} /> });
const ProductRecentlyViewed = dynamic(() => import(/* webpackChunkName: "product-recent-viewed-products" */"../../component/product/recent-viewed-products/ProductRecentlyViewed"));
const ApparelImageSection = dynamic(() => import(/* webpackChunkName: "apparel-image-section" */"component/product/apparel/ApparelImageSection"));
const ApparelOverview = dynamic(() => import(/* webpackChunkName: "apparel-overview" */"component/product/apparel/ApparelOverview"));
const ApparelShippingInfo = dynamic(() => import(/* webpackChunkName: "apparel-shipping-info" */"component/product/apparel/ApparelShippingInfo"));
const ColorandSize = dynamic(() => import(/* webpackChunkName: "color-size-section" */ "component/product/apparel/ColarandSize"));

interface IProps {
  product: ICompactProduct;
  staticContent: IPageContent;
}

function ProductRegular(props: IProps) {
  const {
    product,
    details,
    productOverviewSectionRef,
    productImages,
    productImagesV2,
    handleProductOverviewSectionExpand,
    staticContent,
    productReviews,
    productQuestions,
    components,
    defaultVehicle,
    manufacturerPageUrl,
    installationTips,
    handleSaveItemForLater,
    pageType,
    limitedQuantityDetails,
    breadCrumbsV2,
    fitmentOptionsDetails,
    showNewGuaranteedFitDesign,
  } = props;

  const dispatch = useDispatch();

  const isApparel = isApparelProduct(product);
  const { businessCustomer, loggedIn } = useSelector((state: RootState) => state.authenticationState);
  const { selectedVehicle } = useSelector((state: RootState) => state.garage);
  // const { previousPageType } = useSelector((state: RootState) => state.utils);
  const singleBreadcrumbList = breadCrumbsV2[0] || [];
  let categoryUrl = "";
  singleBreadcrumbList.forEach((item: any) => {
    if (item?.url) {
      categoryUrl = item.url;
    }
  });

  const { pageDetails } = staticContent;
  const { links, videos } = staticContent;

  const { productDescription } = links || {};

  const [showStickyHeader, setShowStickyHeader] = useState(false);
  const [showApparelSection, setShowApparelSection] = useState("product_description");
  const apparelPurchaseActionsRef = useRef<HTMLDivElement>(null);

  const isDesktop = useCustomScreenSize({
    minWidth: matchMediaConst.LG_MIN,
  });
  useEffect(() => {
    function listenScrollEvent() {
      if (isApparel) {
        setShowStickyHeader(Boolean(apparelPurchaseActionsRef.current && apparelPurchaseActionsRef.current.getBoundingClientRect().bottom <= 0));
        return;
      }

      setShowStickyHeader(window.pageYOffset > 330);
    }

    listenScrollEvent();
    window.addEventListener("scroll", listenScrollEvent);
    return () => {
      window.removeEventListener("scroll", listenScrollEvent);
    };
  }, [isApparel]);

  const {
    RatingUI, ProductCommon, PageContainer, ProductPageHeader,
    ShippingCheckEstimatorNew, ProductDeliveryMethodSelector, HelpDesk,
  } = components;
  const manufactureImageUrl = product.manufacturerSmallImageUrl || pageDetails?.manufacturerImageUrl;
  const manufacturer = product.manufacturer || pageDetails?.manufacturer;
  const { openGuaranteedFitProductOverlay, guaranteedFitProducts } = product;

  function sideBarHandler(data: any) {
    dispatch(openModal("sidebar", {
      ...{
        productOverviewSectionRef,
        handleProductOverviewSectionExpand,
      },
      ...data,
    }));
  }

  function handleApparelSectionJump(section: string) {
    setShowApparelSection(section);

    window.setTimeout(() => {
      const element = document.getElementById("apparel_overview_container");

      if (element) {
        element.scrollIntoView({ behavior: "smooth" });
      }
    }, 0);
  }

  const { t } = useTranslation("product");

  return (
    <>
      {
        showStickyHeader && (
          <ProductPageStickyHeader
            product={product}
            productOverviewSectionRef={productOverviewSectionRef}
            handleProductOverviewSectionExpand={handleProductOverviewSectionExpand}
            {...{
              handleSaveItemForLater,
              pageType,
              staticContent,
              limitedQuantityDetails,
              handleApparelSectionJump,
            }}
          />
        )
      }

      <PageContainer
        isApparelLayout={isApparel}
        className="d-flex flex-column"
      >
        <GuaranteedFitProducts guaranteedFitProducts={guaranteedFitProducts} productNotFit={product} />

        <HideIf show={!isApparel}>
          <ProductPageHeader
            product={product}
            {...{
              extendedDescription: product.extendedDescription,
              handleSaveItemForLater,
              breadCrumbsV2,
              pageType,
              productQuestions,
              productReviews,
              staticContent,
              manufacturer,
              limitedQuantityDetails,
              manufacturerPageUrl,
            }}
            showPrice
            desktopHeaderRightSection={(
              <>
                <div>
                  <div className={styles.header_right_section}>
                    <ProductHeaderRightSection
                      {...{
                        limitedQuantityDetails,
                      }}
                      product={product}
                      setSyncProduct
                    />
                  </div>
                </div>
              </>
            )}
          />
        </HideIf>

        {/* <HideIf show={isApparel}>
          <Breadcrumbs
            {...{
              category: product.category,
              primaryCategory: product.primaryCategory,
              breadCrumbsV2,
              breadcrumbs: staticContent.breadcrumbs,
              staticContent,
              partNumber: product.partNumber,
              openGuaranteedFitProductOverlay,
              className: "m-l-15 m-t-15",
            }}
            showBackToResult={CATEGORY_PAGE_TYPES.includes(previousPageType)}
          />
        </HideIf> */}

        <div className={classNames("row row-padding product-section m-t-15 m-lr-0 p-lr-0 d-flex", styles.product_section, { [`${styles.apparel_product_section} flex-wrap`]: isApparel })}>
          <div className={classNames(`${isApparel ? "col-lg-7 col-md-7" : "col-lg-6 col-md-6"} col-xs-12 d-flex flex-column`, {
            [styles.apparel_image_column]: isApparel,
          })}
          >
            <div className={styles.sticky_left_product}>
              <HideIf show={isApparel}>
                <ApparelImageSection
                  images={
                    productImagesV2?.length > 0 ? productImagesV2[0]?.urls || [] : []
                  }
                  manufacturerImage={manufactureImageUrl}
                  title={product?.description}
                  productImagesV2={productImagesV2}
                  product={product}
                  videos={videos}
                />
              </HideIf>

              <HideIf show={!isApparel}>
                <ProductImageSection
                  {...{
                    outOfStock: product.outOfStock,
                    thumbnail: product.thumbnail,
                    description: product.description,
                    price: product.price,
                    productImages,
                    productImagesV2,
                    manufactureImageUrl,
                    manufacturer,
                    imageTitle: getProductHeading(product),
                  }}
                  product={product}
                  productImages={productImages}
                  staticContent={staticContent}
                  largeImage={isDesktop}
                  isRegularDesktop
                />
              </HideIf>
            </div>
          </div>

          <div className={classNames(`${isApparel ? "col-lg-5 col-md-5 col-xs-12" : "col-lg-6 col-md-6"}`, {
            [styles.apparel_info_column]: isApparel,
          })}
          >
            <div className={`${isApparel ? styles.sticky_right_product : ""}`}>
              <HideIf show={isApparel}>
                <ProductPageHeader
                  product={product}
                  {...{
                    extendedDescription: product.extendedDescription,
                    handleSaveItemForLater,
                    breadCrumbsV2,
                    pageType,
                    productQuestions,
                    productReviews,
                    staticContent,
                    manufacturer,
                    limitedQuantityDetails,
                    manufacturerPageUrl,
                  }}
                  showPrice
                />
              </HideIf>
              <div className="row">
                <div className="col-md-1 col-lg-1" />
                <div className="centered col-md-11 col-lg-11 p-lr-0">
                  <RatingUI
                    {...{
                      PRODUCT_ID: product.upc || product.sku,
                    }}
                  />
                </div>
              </div>
              <HideIf show={!isApparel}>
                <>
                  <div className={classNames({
                    [styles.fitment_correct_product_container]: selectedVehicle,
                  })}
                  >
                    <ProductFitment
                      staticContent={staticContent}
                      product={product}
                      handleProductOverviewSectionExpand={
                        handleProductOverviewSectionExpand
                      }
                      defaultVehicle={defaultVehicle}
                      fitmentOptionsDetails={fitmentOptionsDetails}
                      showMore
                      isLongErrMsg
                      isHorizontalVehicleSelector
                    />
                    <HideIf show={openGuaranteedFitProductOverlay}>
                      <ViewCorrectProducts product={product} />
                    </HideIf>
                    <HideIf show={showNewGuaranteedFitDesign}>
                      <ViewCorrectProducts
                        product={product}
                        correctPartButtonText="CHECK THESE PRODUCTS"
                        correctPartButtonType={BUTTON_TYPE.OUTLINE}
                        noFitmentBorder
                        categoryUrl={categoryUrl}
                      />
                    </HideIf>
                  </div>
                  <hr className="m-0" style={{ display: businessCustomer ? "none" : "block" }} />
                </>
              </HideIf>

              <HideIf show={isApparel}>
                <>
                  <ColorandSize
                    alternateProducts={product?.alternateProducts}
                    filterableAttributes={product?.filterableAttributes}
                    handleSectionClick={handleApparelSectionJump}
                    packOf={product?.packOf}
                    outOfStock={product?.outOfStock}
                    links={links}
                  />
                  <div ref={apparelPurchaseActionsRef}>
                    <ProductHeaderRightSection
                      {...{
                        limitedQuantityDetails,
                      }}
                      product={product}
                      setSyncProduct
                    />
                  </div>
                  <HorizontalSeparator />
                </>
              </HideIf>

              <HideIf show={!businessCustomer}>
                <PAText
                  component="span"
                  fontSize={FONT_SIZE[2]}
                  className="m-t-10 m-b-10"
                  inlineBlock
                  bold
                  color="black"
                >
                  {t("Buy Online & Select Delivery")}
                </PAText>
                <ShippingCheckEstimatorNew title={t("Check options for")} />
                <ProductDeliveryMethodSelector product={product} />
              </HideIf>

              <HideIf show={productDescription?.length && !isApparel}>
                <HideIf show={!loggedIn}>
                  <hr style={{ marginTop: 10 }} />
                </HideIf>

                <PAText
                  component="span"
                  fontSize={FONT_SIZE[2]}
                  className="m-t-10 m-b-20"
                  inlineBlock
                  bold
                  color="black"
                >
                  {t("Product Description")}
                </PAText>
                <div className={styles.apparel_product_description}>
                  <ShowMoreAndLess
                    onClick={() => handleApparelSectionJump("overview")}
                    contentDescription={(
                      <ProductDescription productDescription={productDescription} sku={product.sku} />
                    )}
                  />
                </div>
              </HideIf>

              <ProductOverview
                {...{
                  product,
                  details,
                  productReviews,
                  productQuestions,
                  staticContent,
                  handleProductOverviewSectionExpand,
                  manufacturer,
                  manufactureImageUrl,
                  manufacturerPageUrl,
                  installationTips,
                }}
                isCompact={false}
                ref={productOverviewSectionRef}
              />
              <HideIf show={isApparel}>
                <ApparelShippingInfo />
              </HideIf>
              <HideIf show={!isApparel && !businessCustomer}>
                <HelpDesk />
              </HideIf>
              <HideIf show={!businessCustomer}>
                <Sitejabber showGoogleBadgeProps />
              </HideIf>
            </div>
          </div>
        </div>

        <HideIf show={isApparel}>
          <ApparelOverview
            product={product}
            showApparelSection={showApparelSection}
            setShowApparelSection={setShowApparelSection}
            links={staticContent.links}
          />
        </HideIf>
        <ProductCommon
          product={product}
          staticContent={staticContent}
          sideBarHandler={sideBarHandler}
          productRecentlyViewedSection={(
            <VizSensor minHeight={522}>
              <div
                className="recently-viewed-section hidden-print hidden-xs"
              >
                <ProductRecentlyViewed
                  ignoreSku={product.sku}
                  sideBarHandler={sideBarHandler}
                  productPage
                />
              </div>
            </VizSensor>
          )}
          showMerchandiseSlider
        />
      </PageContainer>
    </>
  );
}

export default ProductRegular;
